import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import { Avatar, Badge, Box, Button, Flex, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import { NavArrowRight } from 'iconoir-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

export type IntegrationCardVariant = 'row' | 'detail';

/**
 * `tile`: the image is a full square logo and fills the avatar.
 * `mark`: a transparent logo mark (Claude, ChatGPT) drawn inset on white, so the avatar's
 * grey fallback never shows through and the mark is not cropped by the circle.
 */
export type IntegrationIconStyle = 'tile' | 'mark';

const MARK_ROOT = { bg: 'white', borderWidth: '1px', borderColor: 'border' } as const;
const MARK_IMAGE = { objectFit: 'contain', p: '17%' } as const;

interface IntegrationCardProps {
  name: string;
  description: string;
  icon: string;
  iconStyle?: IntegrationIconStyle;
  isActive: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onToggle: () => void;
  showDisconnectButton?: boolean; // Connections with their own per-grant revoke controls omit it.
  showConfirmDialog?: boolean; // Allow bypassing the built-in confirm dialog
  connectedAs?: string; // Optional: show connected account/page name
  connectedLabel?: string; // Optional: status label shown next to the connected value
  connectedLabelColorPalette?: 'green' | 'orange' | 'red' | 'gray';
  /** Refresh-style action shown inline on the row AND on the detail page (e.g. Reconnect). */
  inlineAction?: React.ReactNode;
  /** Config action shown on the detail page only (e.g. Manage access, Manage senders). */
  detailAction?: React.ReactNode;
  /** Policy disclosure visible in both variants, including before connection. */
  disclosure?: React.ReactNode;
  /** Config / context rendered on the detail page only. */
  children?: React.ReactNode;
  /** `row` inside the list, `detail` on the dedicated sub-page. */
  variant?: IntegrationCardVariant;
  /** When set (row variant), the row exposes a chevron linking to the detail page. */
  detailHref?: string;
}

/**
 * Renders a single integration in one of two shapes:
 * - `row`: a compact stack-list line (icon + name + description, inline
 *   primary action, chevron to the detail page). Destructive / config actions
 *   live on the detail page, not here.
 * - `detail`: the dedicated sub-page surface (large header + every action +
 *   config sections passed as children).
 *
 * The connect / disconnect / reconnect logic stays in the per-platform
 * component; this component only owns presentation and the confirm dialog.
 */
const IntegrationCard: React.FC<IntegrationCardProps> = ({
  name,
  description,
  icon,
  iconStyle = 'tile',
  isActive,
  disabled = false,
  isLoading = false,
  onToggle,
  showConfirmDialog = true,
  showDisconnectButton = true,
  connectedAs,
  connectedLabel,
  connectedLabelColorPalette = 'green',
  inlineAction,
  detailAction,
  disclosure,
  children,
  variant = 'row',
  detailHref,
}) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const rootStyle = iconStyle === 'mark' ? MARK_ROOT : undefined;
  const imageStyle = iconStyle === 'mark' ? MARK_IMAGE : undefined;

  const handleToggleClick = () => {
    if (isActive && showConfirmDialog) {
      setIsDialogOpen(true);
    } else {
      onToggle();
    }
  };

  const toggleButton = (
    <Button
      onClick={handleToggleClick}
      disabled={disabled || isLoading}
      loading={isLoading}
      variant={isActive ? 'outline' : 'subtle'}
      colorPalette="gray"
      size="sm"
    >
      {isActive ? t('integrations.card.disconnect') : t('integrations.card.connect')}
    </Button>
  );

  const status =
    isActive && connectedAs ? (
      <HStack gap={2} minW={0}>
        {connectedLabel && (
          <Badge size="xs" colorPalette={connectedLabelColorPalette}>
            {connectedLabel}
          </Badge>
        )}
        <Text fontSize="sm" color="fg.muted" lineClamp={1}>
          {connectedAs}
        </Text>
      </HStack>
    ) : null;

  const confirmDialog = showConfirmDialog && (
    <ConfirmDialog
      isOpen={isDialogOpen}
      title={t('integrations.card.disconnectTitle', { name })}
      description={t('integrations.card.disconnectDescription', { name })}
      onConfirm={() => {
        setIsDialogOpen(false);
        onToggle();
      }}
      onCancel={() => setIsDialogOpen(false)}
    />
  );

  if (variant === 'detail') {
    return (
      <>
        <Stack gap={6}>
          <Flex align="center" gap={4} minW={0}>
            <Avatar.Root size="xl" {...rootStyle}>
              <Avatar.Fallback name={name} />
              <Avatar.Image src={icon} {...imageStyle} />
            </Avatar.Root>
            <Box minW={0}>
              <Heading size="xl">{name}</Heading>
              <Text fontSize="sm" color="fg.muted">
                {description}
              </Text>
            </Box>
          </Flex>

          {status}

          {disclosure}

          <HStack gap={3} flexWrap="wrap">
            {!isActive && toggleButton}
            {isActive && inlineAction}
            {isActive && detailAction}
            {isActive && showDisconnectButton && toggleButton}
          </HStack>

          {children}
        </Stack>
        {confirmDialog}
      </>
    );
  }

  // Connected integrations link to their detail / config page; the whole row is
  // clickable (stretched link) and only they show the chevron affordance.
  const opensDetail = isActive && Boolean(detailHref);
  const primaryAction = isActive ? inlineAction : toggleButton;

  return (
    <>
      <Flex
        align="center"
        gap={3}
        px={{ base: 3, md: 4 }}
        py={{ base: 3, md: 4 }}
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
        position="relative"
        cursor={opensDetail ? 'pointer' : undefined}
        transition="background 0.15s ease"
        _hover={opensDetail ? { bg: 'blackAlpha.50' } : undefined}
      >
        {isActive && detailHref && (
          <Box asChild position="absolute" inset={0}>
            <RouterLink to={detailHref} aria-label={t('integrations.card.openDetails', { name })} />
          </Box>
        )}

        <Flex align="center" gap={4} flex="1" minW={0}>
          <Avatar.Root size="md" {...rootStyle}>
            <Avatar.Fallback name={name} />
            <Avatar.Image src={icon} {...imageStyle} />
          </Avatar.Root>
          <Box flex="1" minW="120px">
            <Heading size="md" lineClamp={1}>
              {name}
            </Heading>
            <Text fontSize="sm" color="fg.muted" lineClamp={1}>
              {description}
            </Text>
          </Box>
        </Flex>

        <HStack gap={3} flexShrink={0}>
          {status}
          {primaryAction && (
            <Box position="relative" zIndex={1}>
              {primaryAction}
            </Box>
          )}
          {opensDetail && (
            <Box display="inline-flex" alignItems="center" color="fg.muted" aria-hidden="true">
              <NavArrowRight width={20} height={20} />
            </Box>
          )}
        </HStack>
      </Flex>
      {disclosure && (
        <Box px={{ base: 3, md: 4 }} pb={{ base: 3, md: 4 }}>
          {disclosure}
        </Box>
      )}
      {confirmDialog}
    </>
  );
};

export default IntegrationCard;
